// AssistantContext — owns the global platform assistant: its drawer's open
// state AND its chat state machine (plan 2026-07-28 §5; state lifted here by
// plan 2026-08-02).
//
// Gating: universal surface (no module gate) behind the account feature flag
// `platform_assistant`. When the flag is off there is no opener, no hotkey and
// no drawer, `useAssistant().enabled` is false so AppHeader hides its button,
// and `ask()` is a no-op. The chat hook still runs (hooks cannot be
// conditional) but stays inert: its only fetch is gated on `opened`, which is
// forced false while disabled.
//
// The provider lives inside the persistent AuthenticatedShell, so open state and
// the conversation survive route changes within the SPA session. That is also
// why the chat state lives HERE rather than in the drawer: the dashboard's chat
// bar needs to start a turn, and a surface that only opens the drawer cannot
// tell the caller whether a turn is already streaming.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { useAuth } from "@/context/AuthContext";
import { AssistantDrawer } from "@/components/assistant/AssistantDrawer";
import { useAssistantChat } from "@/hooks/useAssistantChat";

interface AssistantContextValue {
  /** True when the account has the `platform_assistant` feature flag on. */
  enabled: boolean;
  opened: boolean;
  /** True while a turn is streaming. Callers disable their own submit affordances. */
  isStreaming: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  /**
   * Open the drawer on a FRESH conversation and send `message` immediately.
   * Returns true when the turn was started.
   *
   * No-ops when the assistant is disabled or a turn is already streaming — the
   * guard lives here rather than in each caller's `disabled` attribute, because
   * a disabled attribute cannot stop a programmatic call and `isStreaming` is a
   * render behind.
   */
  ask: (message: string) => boolean;
}

const AssistantContext = createContext<AssistantContextValue>({
  enabled: false,
  opened: false,
  isStreaming: false,
  open: () => {},
  close: () => {},
  toggle: () => {},
  ask: () => false,
});

export function AssistantProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const enabled = user?.activeAccount?.featureFlags?.platform_assistant === true;

  const [opened, setOpened] = useState(false);

  const open = useCallback(() => setOpened(true), []);
  const close = useCallback(() => setOpened(false), []);
  const toggle = useCallback(() => setOpened((o) => !o), []);

  const chat = useAssistantChat(opened);

  // Stable across renders: newConversation/send come from useCallback, and the
  // streaming check reads a ref — so `ask` does not change identity per token,
  // and the context value below does not churn while a turn streams.
  const { newConversation, send, isStreamingRef } = chat;
  const ask = useCallback(
    (message: string) => {
      if (!enabled) return false;
      if (!message.trim()) return false;
      // Mirrors AssistantInput: a turn in flight is never silently replaced.
      if (isStreamingRef.current) return false;
      newConversation();
      setOpened(true);
      void send(message);
      return true;
    },
    [enabled, newConversation, send, isStreamingRef],
  );

  // Global Cmd/Ctrl+J shortcut (deliberately not Cmd+K). Registered only while
  // the flag is on; fires regardless of focus so it can also close the drawer
  // from within its own composer.
  useEffect(() => {
    if (!enabled) return;
    const handler = (e: KeyboardEvent) => {
      if (
        (e.metaKey || e.ctrlKey) &&
        !e.shiftKey &&
        !e.altKey &&
        (e.key === "j" || e.key === "J")
      ) {
        e.preventDefault();
        setOpened((o) => !o);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [enabled]);

  // If the flag flips off (e.g. account switch), make sure the drawer is closed.
  useEffect(() => {
    if (!enabled && opened) setOpened(false);
  }, [enabled, opened]);

  // Deliberately does NOT spread `chat` — that object is rebuilt every render,
  // so every streamed token would re-render every consumer (AppHeader, the
  // dashboard). Only the fields other surfaces actually need are published.
  const isStreaming = chat.isStreaming;
  const value = useMemo<AssistantContextValue>(
    () => ({ enabled, opened, isStreaming, open, close, toggle, ask }),
    [enabled, opened, isStreaming, open, close, toggle, ask],
  );

  return (
    <AssistantContext.Provider value={value}>
      {children}
      {enabled && <AssistantDrawer opened={opened} onClose={close} chat={chat} />}
    </AssistantContext.Provider>
  );
}

export function useAssistant() {
  return useContext(AssistantContext);
}
